// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import { awOrange } from 'Utils/themes';
import { resolveProjectStatus, ProjectStatusKind } from 'Utils/projectStatus';
import { nameProjectProgressStep } from 'Utils/projectProgress';
import { kmlOnlyHoldWord } from 'Utils/orderStatus';

export type PinStatusKind = ProjectStatusKind;

export interface IPinStatusChip {
  label: string;
  kind: PinStatusKind;
}

// What a chip reads about a project: its representative order's status, and for a KML-only hold the fields that name
// the hold (flattened onto a dashboard coordinate, or picked off the folder page's raw orders by selectProjectInfo).
export interface IStatusChipProject {
  orderStatus?: number;
  isEstimate?: boolean;
  kmlOnly?: boolean;
  kmlOnlyOwnData?: boolean;
  kmlOnlyLaunched?: boolean;
  ownDataReceived?: boolean;
}

// A KML-only hold that Start Processing has not launched is placed but not processing: in place of "In Progress" its
// chip reads what the project page and Order History read - "Sourcing data", "Awaiting your data" or "Data received"
// (AUT-6630). Every other reading is the shared one, untouched.
const holdChipLabel = (project: IStatusChipProject, kind: ProjectStatusKind): string | null => (
  kind === 'inProgress' ? kmlOnlyHoldWord(project) : null
);

// The chip half of the shared status reading - the grid card takes the same answer and renders
// its step instead. Kept as a named function because the map and rail want a chip, not a status.
export const getPinStatusChip = (project: IStatusChipProject): IPinStatusChip => {
  const { label, kind } = resolveProjectStatus(project);
  return { label: holdChipLabel(project, kind) ?? label, kind };
};

// The chip with the pipeline stage named alongside it: "In Progress - QC". For the surfaces
// that draw no pipeline of their own - the rail's cards and the pin card - so the stage is still
// readable there; the grid card draws its stepper and takes the plain chip above instead.
// Only an in-progress project has a stage: a delivered or canceled one has left the pipeline,
// and naming a stage for it would describe work that is no longer happening.
export const getStatusChipWithPhase = (project: IStatusChipProject): IPinStatusChip => {
  const { label, kind, step } = resolveProjectStatus(project);
  const holdLabel = holdChipLabel(project, kind);
  if (holdLabel) return { label: holdLabel, kind };
  const phase = kind === 'inProgress' ? nameProjectProgressStep(step) : null;
  return { label: phase ? `${label} - ${phase}` : label, kind };
};

// One dot-color source for every surface that renders a pin status (rail cards, map popup):
// the Figma pairs "Completed" with a green dot; in-progress reads accent blue.
export const getStatusDotColor = (theme: any, kind: PinStatusKind): string => {
  const dotColors: Record<PinStatusKind, string> = {
    delivered: theme.palette.success.main,
    inProgress: theme.palette.primary.main,
    estimate: awOrange,
    canceled: theme.palette.darkGrey,
    notSubmitted: theme.palette.darkGrey,
  };
  return dotColors[kind];
};
